import { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { CreateCampaignTemplateDto, CampaignType } from '../../types/campaignTemplate';
import WhatsAppPreview from './WhatsAppPreview';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialWabaAccountId?: string;
}

interface GlobalParam {
  key: string;
  value: string;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: any[];
}

export function CreateTemplateModal({
  isOpen,
  onClose,
  onSuccess,
  initialWabaAccountId,
}: CreateTemplateModalProps) {
  const { accessToken } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // WhatsApp Templates
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateCampaignTemplateDto>({
    name: '',
    description: '',
    wabaAccountId: initialWabaAccountId || '',
    templateName: '',
    templateLanguage: 'pt_BR',
    type: 'broadcast',
    defaultGlobalParams: {},
  });

  // Global params editor
  const [globalParams, setGlobalParams] = useState<GlobalParam[]>([]);

  // Load WhatsApp templates automatically when modal opens
  useEffect(() => {
    if (isOpen && initialWabaAccountId && accessToken) {
      fetchWhatsAppTemplates(initialWabaAccountId);
    }
  }, [isOpen, initialWabaAccountId, accessToken]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        wabaAccountId: initialWabaAccountId || '',
        templateName: '',
        templateLanguage: 'pt_BR',
        type: 'broadcast',
        defaultGlobalParams: {},
      });
      setGlobalParams([]);
      setError(null);
      setSelectedTemplate(null);
    }
  }, [isOpen, initialWabaAccountId]);

  const fetchWhatsAppTemplates = async (wabaAccountId: string) => {
    if (!accessToken) return;

    setLoadingTemplates(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/templates?wabaAccountId=${wabaAccountId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao carregar templates');
      }

      const data = await response.json();

      if (data.success) {
        // Filtrar apenas templates aprovados
        const approved = (data.data.templates || data.data || []).filter((t: WhatsAppTemplate) =>
          t.status === 'APPROVED' || t.status.startsWith('ACTIVE')
        );

        setWhatsappTemplates(approved);
      } else {
        setError(data.message || 'Erro ao carregar templates');
        setWhatsappTemplates([]);
      }
    } catch (err: any) {
      console.error('Error loading WhatsApp templates:', err);
      setError('Erro ao carregar templates WhatsApp');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Extrair variáveis globais dos componentes do template
  // IMPORTANTE: Variáveis de BODY não são incluídas pois são sempre preenchidas via CSV
  const extractGlobalVariables = (template: WhatsAppTemplate): { variables: string[], defaults: Record<string, string> } => {
    const variables: string[] = [];
    const defaults: Record<string, string> = {};

    template.components.forEach((component: any) => {
      if (component.type === 'HEADER') {
        if (component.format === 'TEXT') {
          // BLOQUEIO TEMPORÁRIO: Header de texto com variáveis não é mais permitido
          // Não extrair variáveis de header TEXT ({{1}}, {{2}}, etc.)
          // Se existir um template antigo com variáveis, elas serão ignoradas
          // const matches = component.text?.match(/\{\{(\d+)\}\}/g) || [];
          // matches.forEach((match: string) => {
          //   const num = match.replace(/\{\{|\}\}/g, '');
          //   variables.push(`header_${num}`);
          // });
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) {
          // Header de mídia (imagem, vídeo, documento) sempre tem uma variável dinâmica
          // A variável representa a URL do arquivo a ser enviado
          variables.push(`header_media`);

          // Extrair URL padrão da mídia (exemplo do template)
          if (component.example?.header_handle?.[0]) {
            defaults['header_media'] = component.example.header_handle[0];
          }
        }
      }

      // BODY variáveis não são parâmetros globais - são preenchidas via CSV
      // if (component.type === 'BODY') {
      //   const matches = component.text?.match(/\{\{(\d+)\}\}/g) || [];
      //   matches.forEach((match: string) => {
      //     const num = match.replace(/\{\{|\}\}/g, '');
      //     variables.push(`body_${num}`);
      //   });
      // }

      if (component.type === 'BUTTONS') {
        // Procurar por variáveis em botões dinâmicos
        component.buttons?.forEach((button: any, idx: number) => {
          if (button.type === 'URL' && button.url?.includes('{{')) {
            variables.push(`button_${idx + 1}_url`);
          }
        });
      }
    });

    return { variables, defaults };
  };

  const handleTemplateSelect = (templateName: string) => {
    const template = whatsappTemplates.find(t => t.name === templateName);

    if (template) {
      setSelectedTemplate(template);
      setFormData(prev => ({
        ...prev,
        templateName: template.name,
        templateLanguage: template.language,
      }));

      // Extrair variáveis globais e valores padrão
      const { variables, defaults } = extractGlobalVariables(template);

      // Criar parâmetros com valores padrão quando disponíveis
      const params: GlobalParam[] = variables.map(varName => ({
        key: varName,
        value: defaults[varName] || '', // Usa valor padrão se existir
      }));

      setGlobalParams(params);
    }
  };


  const handleRemoveParam = (key: string) => {
    setGlobalParams(globalParams.filter(p => p.key !== key));
  };

  const handleUpdateParam = (key: string, newValue: string) => {
    setGlobalParams(globalParams.map(p =>
      p.key === key ? { ...p, value: newValue } : p
    ));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome do template é obrigatório');
      return false;
    }

    if (!formData.wabaAccountId) {
      setError('Selecione uma conta WABA');
      return false;
    }

    if (!formData.templateName.trim()) {
      setError('Selecione um template WhatsApp');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !accessToken) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert global params array to object
      const defaultGlobalParams: Record<string, any> = {};
      globalParams.forEach(param => {
        defaultGlobalParams[param.key] = param.value;
      });

      const dataToSubmit: CreateCampaignTemplateDto = {
        ...formData,
        defaultGlobalParams: Object.keys(defaultGlobalParams).length > 0
          ? defaultGlobalParams
          : undefined,
      };

      const response = await apiClient.createCampaignTemplate(dataToSubmit, accessToken);

      if (response.success) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Extract template components for preview
  const headerComponent = selectedTemplate?.components?.find(c => c.type === 'HEADER');
  const bodyComponent = selectedTemplate?.components?.find(c => c.type === 'BODY');
  const footerComponent = selectedTemplate?.components?.find(c => c.type === 'FOOTER');
  const buttonsComponent = selectedTemplate?.components?.find(c => c.type === 'BUTTONS');

  const bodyExamples = bodyComponent?.example?.body_text?.[0] || [];
  const headerExample = headerComponent?.example?.header_text?.[0]?.[0] || '';
  const headerMediaUrl = headerComponent?.example?.header_handle?.[0] || '';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-bg-1 border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-text-1">Criar Campanha de Disparo</h2>
            <p className="text-sm text-text-2 mt-1">
              Configure uma campanha reutilizável para seus disparos
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-3 hover:text-text-2 text-2xl leading-none"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-6 px-6 py-6">
          {/* Sidebar Esquerda - Preview WhatsApp */}
          <div className="w-[400px] flex-shrink-0">
            <div className="sticky top-6">
              <h3 className="text-sm font-semibold text-text-2 mb-3">
                📱 Preview do Template
              </h3>
              {selectedTemplate ? (
                <WhatsAppPreview
                  headerFormat={headerComponent?.format as any}
                  headerText={headerComponent?.format === 'TEXT' ? headerComponent?.text : undefined}
                  headerExample={headerExample}
                  headerMediaUrl={headerMediaUrl}
                  bodyText={bodyComponent?.text || ''}
                  bodyExamples={bodyExamples}
                  footerText={footerComponent?.text}
                  buttons={buttonsComponent?.buttons || []}
                  highlightVariables
                />
              ) : loadingTemplates ? (
                <div className="bg-bg-3 rounded-lg p-8 text-center">
                  <span className="animate-spin inline-block text-2xl">⏳</span>
                  <p className="text-sm text-text-3 mt-2">Carregando templates...</p>
                </div>
              ) : (
                <div className="bg-bg-3 rounded-lg p-8 text-center">
                  <p className="text-sm text-text-3">Selecione um template para ver o preview</p>
                </div>
              )}
            </div>
          </div>

          {/* Área Central - Formulário */}
          <div className="flex-1">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
            <div className="bg-danger/10 border border-danger/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <span className="text-danger">⚠️</span>
                <p className="text-danger text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Nome do Template */}
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">
              Nome do Template <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              placeholder="Ex: Black Friday 2024"
              maxLength={255}
              required
            />
            <p className="text-xs text-text-3 mt-1">
              Nome único para identificar este template
            </p>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">
              Descrição (Opcional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              placeholder="Ex: Campanha promocional com 50% de desconto"
              rows={3}
            />
          </div>

          {/* Template WhatsApp - Dropdown com templates aprovados */}
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">
              Template WhatsApp <span className="text-danger">*</span>
            </label>
            <select
              value={formData.templateName}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full px-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              required
              disabled={loadingTemplates}
            >
              <option value="">
                {loadingTemplates
                  ? 'Carregando templates...'
                  : whatsappTemplates.length === 0
                  ? 'Nenhum template aprovado encontrado'
                  : 'Selecione um template aprovado'
                }
              </option>
              {whatsappTemplates.map((template) => (
                <option key={template.id} value={template.name}>
                  {template.name} ({template.language}) - {template.category}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-3 mt-1">
              Apenas templates aprovados pelo Meta são exibidos
            </p>
          </div>

          {/* Preview do template selecionado */}
          {selectedTemplate && (
            <div className="bg-info/10 border border-info rounded-lg p-4">
              <h4 className="text-sm font-semibold text-accent-2 mb-2">Template Selecionado:</h4>
              <div className="text-sm text-accent-2 space-y-1">
                <p><strong>Nome:</strong> {selectedTemplate.name}</p>
                <p><strong>Categoria:</strong> {selectedTemplate.category}</p>
                <p><strong>Idioma:</strong> {selectedTemplate.language}</p>
                <p><strong>Status:</strong> {selectedTemplate.status}</p>
              </div>
            </div>
          )}

          {/* Campaign Type */}
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">
              Tipo de Campanha
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Broadcast - Disponível */}
              <label
                className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.type === 'broadcast'
                    ? 'border-accent-1 bg-info/10'
                    : 'border-border-1 hover:bg-bg-hover'
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value="broadcast"
                  checked={formData.type === 'broadcast'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as CampaignType })}
                  className="sr-only"
                />
                <span className="text-sm font-medium">
                  Broadcast
                </span>
              </label>

              {/* Scheduled - Em Desenvolvimento */}
              <label
                className="flex flex-col items-center justify-center p-3 border-2 rounded-lg cursor-not-allowed border-border-1 bg-bg-2 opacity-60"
                title="Funcionalidade em desenvolvimento"
              >
                <span className="text-sm font-medium text-text-3">
                  Agendada
                </span>
                <span className="text-xs text-text-3 mt-1">
                  Em Desenvolvimento
                </span>
              </label>
            </div>
          </div>

          {/* Global Parameters - Só mostra se houver variáveis detectadas */}
          {globalParams.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-2 mb-2">
                Parâmetros Globais Detectados
              </label>
              <p className="text-xs text-text-3 mb-3">
                O template selecionado possui variáveis. Defina valores padrão que podem ser sobrescritos em cada execução.
              </p>

              {/* Existing Params */}
              <div className="bg-bg-2 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                {globalParams.map((param) => (
                  <div key={param.key} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={param.key}
                        disabled
                        className="flex-1 px-3 py-2 bg-bg-3 border border-border-1 rounded text-sm font-mono"
                        title="Nome da variável (automático)"
                      />
                      <span className="text-text-3">=</span>
                      <input
                        type="text"
                        value={param.value}
                        onChange={(e) => handleUpdateParam(param.key, e.target.value)}
                        placeholder="URL da mídia"
                        className="flex-1 px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveParam(param.key)}
                        className="px-3 py-2 text-danger hover:bg-danger/10 rounded text-sm"
                        title="Remover parâmetro"
                      >
                        ×
                      </button>
                    </div>

                    {/* Visualização de mídia para header_media */}
                    {param.key === 'header_media' && param.value && (
                      <div className="ml-2 mt-2">
                        <p className="text-xs text-text-2 mb-1">Visualização:</p>
                        <div className="border border-border-1 rounded-lg overflow-hidden bg-bg-1">
                          {param.value.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            // Visualização de Imagem
                            <img
                              src={param.value}
                              alt="Visualização do cabeçalho"
                              className="max-w-full h-auto max-h-48 object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : param.value.match(/\.(mp4|webm|ogg)$/i) ? (
                            // Preview de Vídeo
                            <video
                              src={param.value}
                              controls
                              className="max-w-full h-auto max-h-48"
                              onError={(e) => {
                                (e.target as HTMLVideoElement).style.display = 'none';
                                (e.target as HTMLVideoElement).nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : (
                            // Preview de Documento ou URL genérica
                            <div className="p-3 text-center">
                              <p className="text-sm text-text-2">📄 {param.value.split('/').pop()}</p>
                              <a
                                href={param.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-accent-1 hover:underline"
                              >
                                Abrir arquivo
                              </a>
                            </div>
                          )}
                          {/* Fallback para erros de carregamento */}
                          <div className="hidden p-3 text-center text-sm text-text-3">
                            ⚠️ Não foi possível carregar o preview
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-text-3 mt-2">
                💡 Deixe vazio para preencher apenas durante a execução
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-info/10 border border-info rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-accent-1">ℹ️</span>
              <div className="text-sm text-accent-2">
                <p className="font-semibold mb-1">Sobre Templates:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Variáveis são detectadas automaticamente do template WhatsApp</li>
                  <li>Parâmetros globais são aplicados a todos os destinatários</li>
                  <li>Você poderá editar parâmetros em cada execução</li>
                </ul>
              </div>
            </div>
          </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-2 border-t px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-text-2 hover:text-text-1 font-medium disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-accent-2 text-white rounded-lg hover:bg-accent-2/90 font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Criando...
              </>
            ) : (
              <>
                <span>✓</span>
                Criar Campanha
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
